import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../format';

export interface StackSeries<K extends string> {
  key: K;
  label: string;
  /** A CSS color, a categorical slot of the chart roles (var(--series-n)). */
  color: string;
}

export interface StackColumn<K extends string> {
  /** Axis label under the column. */
  label: string;
  /** Tooltip title. */
  title: string;
  values: Record<K, number>;
}

const PLOT = 200;
const AXIS = 26;
const LEFT = 44;
const GAP = 2;
const RADIUS = 4;

/** Clean ticks from zero: 1, 2, 2.5 or 5 times a power of ten, about four of them. */
function niceTicks(max: number): number[] {
  if (!(max > 0)) return [0, 1];
  const raw = max / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? raw;
  const ticks = [];
  for (let v = 0; v <= max + step * 0.999; v += step) ticks.push(Math.round(v * 1000) / 1000);
  return ticks;
}

/** A column segment: square at the baseline side, rounded only when it is the data end. */
function segmentPath(x: number, y: number, w: number, h: number, roundTop: boolean) {
  const r = roundTop ? Math.min(RADIUS, w / 2, h) : 0;
  return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
}

/**
 * Stacked columns over time (part-to-whole per period): thin columns grown from one baseline, a
 * 2px surface gap between segments, the data end rounded, hairline grid, a tooltip per column
 * listing every series (also on keyboard focus), the latest total labelled. The legend and the
 * table view are the caller's (values must stay readable without hovering).
 */
export function StackedColumns<K extends string>({
  series,
  columns,
  unit,
  onSelect,
  ariaLabel,
}: {
  series: StackSeries<K>[];
  columns: StackColumn<K>[];
  unit: string;
  onSelect?: (index: number) => void;
  ariaLabel: string;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(640);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.max(280, entry!.contentRect.width)),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const totals = columns.map((c) =>
    series.reduce((sum, s) => sum + Math.max(0, c.values[s.key]), 0),
  );
  const ticks = niceTicks(Math.max(...totals, 0));
  const top = ticks.at(-1)!;
  const plotWidth = width - LEFT - 8;
  const band = plotWidth / columns.length;
  const barWidth = Math.min(24, band * 0.6);
  const y = (v: number) => PLOT - (v / top) * PLOT;
  const everyOther = band < 38;

  const tooltipColumn = hover !== null ? columns[hover] : null;
  const tooltipLeft =
    hover !== null ? Math.min(Math.max(LEFT + band * hover + band / 2 - 80, 0), width - 170) : 0;

  return (
    <div ref={ref} className="wm-viz" style={{ width: '100%' }}>
      <svg
        width={width}
        height={PLOT + AXIS + 8}
        role="img"
        aria-label={ariaLabel}
        style={{ display: 'block', overflow: 'visible' }}
      >
        <g transform="translate(0,8)">
          {ticks.map((v) => (
            <g key={v}>
              <line
                x1={LEFT}
                x2={width - 8}
                y1={y(v)}
                y2={y(v)}
                stroke={v === 0 ? 'var(--viz-axis)' : 'var(--viz-grid)'}
                strokeWidth={1}
              />
              <text x={LEFT - 8} y={y(v) + 4} textAnchor="end" className="wm-viz-tick">
                {formatNumber(v)}
              </text>
            </g>
          ))}
          {columns.map((c, i) => {
            const x = LEFT + band * i + (band - barWidth) / 2;
            let base = 0;
            const visible = series.filter((s) => c.values[s.key] > 0);
            return (
              <g key={c.label + i} opacity={hover === null || hover === i ? 1 : 0.55}>
                {visible.map((s, j) => {
                  const v = c.values[s.key];
                  const y0 = y(base);
                  const y1 = y(base + v);
                  base += v;
                  const last = j === visible.length - 1;
                  // the surface gap separates a segment from the one under it
                  const h = Math.max(0, y0 - y1 - (j > 0 ? GAP : 0));
                  return h > 0 ? (
                    <path key={s.key} d={segmentPath(x, y1, barWidth, h, last)} fill={s.color} />
                  ) : null;
                })}
                {(!everyOther || (columns.length - 1 - i) % 2 === 0) && (
                  <text
                    x={LEFT + band * i + band / 2}
                    y={PLOT + 18}
                    textAnchor="middle"
                    className="wm-viz-tick"
                  >
                    {c.label}
                  </text>
                )}
                {i === columns.length - 1 && totals[i]! > 0 && (
                  <text
                    x={LEFT + band * i + band / 2}
                    y={y(totals[i]!) - 6}
                    textAnchor="middle"
                    style={{ fontSize: 12, fill: 'var(--viz-text-2)', fontWeight: 600 }}
                  >
                    {formatNumber(totals[i]!)}
                  </text>
                )}
                <rect
                  className="wm-viz-hit"
                  x={LEFT + band * i}
                  y={0}
                  width={band}
                  height={PLOT}
                  fill="transparent"
                  tabIndex={0}
                  role="button"
                  aria-label={`${c.title} : ${formatNumber(totals[i]!)} ${unit}`}
                  style={{ cursor: onSelect ? 'pointer' : 'default' }}
                  onPointerEnter={() => setHover(i)}
                  onPointerLeave={() => setHover(null)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                  onClick={() => onSelect?.(i)}
                  onKeyDown={(e) => e.key === 'Enter' && onSelect?.(i)}
                />
              </g>
            );
          })}
        </g>
      </svg>
      {tooltipColumn && (
        <div className="wm-viz-tooltip" style={{ left: tooltipLeft, top: 0 }} role="status">
          <div style={{ marginBottom: 4 }}>{tooltipColumn.title}</div>
          <div style={{ marginBottom: 4 }}>
            <strong>
              {formatNumber(totals[hover!]!)} {unit}
            </strong>{' '}
            {t('charts.total')}
          </div>
          {[...series].reverse().map((s) => (
            <div key={s.key}>
              <span className="wm-viz-key" style={{ background: s.color }} />
              <strong>
                {formatNumber(tooltipColumn.values[s.key])} {unit}
              </strong>{' '}
              {s.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** A ratio against its limit: the fill and the track are two steps of one ramp. */
export function Meter({ value, label }: { value: number | null; label: string }) {
  const pct = value === null ? 0 : Math.max(0, Math.min(1, value)) * 100;
  return (
    <div
      className="wm-viz"
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      style={{ height: 8, borderRadius: 4, background: 'var(--meter-track)', overflow: 'hidden' }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: 'var(--meter-fill)',
          borderRadius: 4,
        }}
      />
    </div>
  );
}

/** Part-to-whole in one thin bar: one segment per series, a 2px surface gap between them. */
export function ShareBar<K extends string>({
  series,
  values,
  label,
}: {
  series: StackSeries<K>[];
  values: Record<K, number>;
  label: string;
}) {
  const total = series.reduce((sum, s) => sum + Math.max(0, values[s.key]), 0);
  const parts = series.filter((s) => values[s.key] > 0);
  return (
    <div
      className="wm-viz"
      role="img"
      aria-label={label}
      style={{ display: 'flex', gap: GAP, height: 10 }}
    >
      {total === 0 ? (
        <div style={{ flex: 1, borderRadius: 4, background: 'var(--viz-grid)' }} />
      ) : (
        parts.map((s, i) => (
          <div
            key={s.key}
            style={{
              flex: values[s.key] / total,
              minWidth: 3,
              background: s.color,
              borderStartStartRadius: i === 0 ? 4 : 0,
              borderEndStartRadius: i === 0 ? 4 : 0,
              borderStartEndRadius: i === parts.length - 1 ? 4 : 0,
              borderEndEndRadius: i === parts.length - 1 ? 4 : 0,
            }}
          />
        ))
      )}
    </div>
  );
}
